/**
 * @description Asynchronously loads videos with responsive support and fallback play button
 */
const lazyLoadVideos = () => {
  // Get all videos with the lazy loading class
  const lazyVideos = [...document.querySelectorAll('video.js-video-loader')];
  if (lazyVideos.length < 1) return;

  // Screen size detection
  const getScreenType = () => ({
    isMobile: window.matchMedia('(max-width: 767px)').matches,
    isTablet: window.matchMedia('(min-width: 768px) and (max-width: 991px)').matches,
    isDesktop: window.matchMedia('(min-width: 992px)').matches
  });

  /**
   * Creates and adds a play button overlay to the video
   * @param {HTMLVideoElement} video - The video element
   */
  function addPlayButton(video) {
    // Prevent duplicate play buttons
    const existingBtn = video.closest('.shopify-section').querySelector('#PLAY_VIDEO_BTN');
    if (existingBtn) return;

    // Check if video is actually playing
    if (video.currentTime > 0 && !video.paused && !video.ended) {
      return;
    }

    let btn = document.createElement("button");
    btn.innerHTML = "&#9658;";
    btn.className = " " + video.parentElement.classList;
    btn.id = "PLAY_VIDEO_BTN";
    btn.setAttribute('aria-label', 'Play video');
    btn.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 9999;
      width: fit-content;
      font-size: 3rem;
      color: #fff;
      transform: translate(-50%, -50%);
      background: transparent;
      border: none;
      cursor: pointer;
    `;
    
    btn.addEventListener('click', () => {
      video.load();
      video.play().catch(() => {
        video.muted = true;
        video.play();
      });
      const playBtn = document.querySelector('#PLAY_VIDEO_BTN');
      if (playBtn) playBtn.remove();
    });
    
    video.closest(".shopify-section").append(btn);
  }

  /**
   * Checks if the video should swap the source for the specific screen
   * @param {HTMLVideoElement} video
   * @returns 
   */
  function checkResponsive(video) {
    const showOn = video.parentElement.dataset.responsiveShow?.split(',');
    const screenType = getScreenType();

    if (!showOn) return setSource(video);

    const conditions = {
      desktop: screenType.isDesktop,
      tablet: screenType.isTablet,
      mobile: screenType.isMobile,
    };

    if (showOn.some((device) => conditions[device.trim()])) {
      setSource(video);
    }
  }
  
  /**
   * Sets video source based on responsive conditions
   * @param {HTMLVideoElement} video - The video element
   * @returns {boolean} - Whether the source was set
   */
  function setSource(video) {
    let children = Array.from(video.children);
    children.forEach(child => {
      if (child.tagName === 'SOURCE' && child.src !== child.dataset.src) {
        child.setAttribute('src', child.dataset.src);
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
      }
    });
  }
  /**
   * Handles video loading and checks playability
   * @param {HTMLVideoElement} video - The video element
   */
  const loadVideo = (video) => {
    checkResponsive(video)

    // Check if video is already loaded and playable
    if (video.readyState >= 3) {
      video.play().catch(() => {
        video.muted = true;
        video.play();
      });
      video.classList.add('video-loaded');
      return;
    }

    // Set up loading timeout and play detection
    const videoLoad = new Promise(resolve => {
      const handleCanPlay = () => {
        resolve('can_play');
        video.removeEventListener('canplaythrough', handleCanPlay);
      };
      video.addEventListener('canplaythrough', handleCanPlay);
    });

    const videoTimeout = new Promise(resolve => {
      setTimeout(() => resolve('timeout'), 2000);
    });

    // Race between loading and timeout
    Promise.race([videoLoad, videoTimeout]).then(result => {
      if (result === 'can_play') {
        video.play().catch(() => {
          video.muted = true;
          video.play();
        });
        setTimeout(() => video.classList.add('video-loaded'), 500);
      } else {
        // Handle timeout - check if video is actually loaded
        video.addEventListener('suspend', () => {
          if (video.readyState === 4) {
            addPlayButton(video);
          }
        });

        video.addEventListener('playing', () => {
          const playBtn = document.querySelector('#PLAY_VIDEO_BTN');
          if (playBtn) playBtn.remove();
        });
      }
    });

    video.load();
  };
  // Check for videos already in viewport
  const checkInitialVideos = () => {
    lazyVideos.forEach(video => {
      const rect = video.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
        loadVideo(video); // Load videos that are in the initial viewport
      }
    });
  };

  // Set up intersection observer if supported
  if ('IntersectionObserver' in window) {
    const lazyVideoObserver = new IntersectionObserver(
      debounce(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            loadVideo(entry.target);
            lazyVideoObserver.unobserve(entry.target);
          }
        });
      }, 100)
    );

    lazyVideos.forEach(video => lazyVideoObserver.observe(video));
    checkInitialVideos();
  } else {
    // Fallback for browsers without IntersectionObserver
    checkInitialVideos();
    
    // Simple scroll-based loading
    window.addEventListener('scroll', debounce(() => {
      lazyVideos.forEach(video => {
        const rect = video.getBoundingClientRect();
        if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
          loadVideo(video);
        }
      });
    }, 100));
  }
};

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', lazyLoadVideos);